import { makeStyles } from 'tss-react/mui'
import { Theme, alpha } from '@mui/material/styles'
import {
  CHAT_GRADIENT_END,
  CHAT_GRADIENT_MIDDLE,
  CHAT_GRADIENT_START,
} from './chatColors'

const FLOATING_INSET = 32
const FLOATING_WIDTH = 600
const FLOATING_HEIGHT = 700
const MODAL_INSET_X = 120
const MODAL_INSET_Y = 100

export const useStyles = makeStyles()((theme: Theme) => ({
  paper: {
    width: '50vw',
    maxWidth: '100vw',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.bg.light,
    overflow: 'hidden',
  },
  /** Fills the column the dock gives it, so the page keeps the rest. */
  dockedPanel: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.bg.light,
    borderLeft: `1px solid ${theme.palette.labraChat.border}`,
    overflow: 'hidden',
  },
  /** Fills the host viewport, for an embedding iframe that has no nav rail. */
  embeddedPaper: {
    width: '100vw',
    maxWidth: '100vw',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.bg.light,
    overflow: 'hidden',
  },
  modalCard: {
    position: 'absolute',
    inset: `${MODAL_INSET_Y}px ${MODAL_INSET_X}px`,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.text.white,
    borderRadius: 12,
    boxShadow: `0px 0px 28px 4px ${alpha(CHAT_GRADIENT_START, 0.25)}`,
    overflow: 'hidden',
    outline: 'none',
  },
  composerGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  disclaimer: {
    textAlign: 'center',
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
    lineHeight: '16px',
    color: '#A1B0B4',
  },
  /** Optional frame for hosts that embed the drawer without a page chrome (e.g. Canvas). */
  borderedPaper: {
    border: `1px solid ${theme.palette.labraChat.borderMuted}`,
    borderTopLeftRadius: theme.spacing(1),
    borderBottomLeftRadius: theme.spacing(1),
  },
  floatingCard: {
    position: 'fixed',
    right: FLOATING_INSET,
    bottom: FLOATING_INSET,
    width: `min(${FLOATING_WIDTH}px, calc(100vw - ${FLOATING_INSET * 2}px))`,
    height: `min(${FLOATING_HEIGHT}px, calc(100vh - ${FLOATING_INSET * 2}px))`,
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.text.white,
    borderRadius: 12,
    // Figma 641:2616 Labra AI floating create panel
    boxShadow: '0px 24px 70px 0px rgba(58, 38, 76, 0.14)',
    overflow: 'hidden',
  },
  splitRow: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'stretch',
  },
  bodyInSplit: {
    flex: '0 0 var(--chat-split)',
    minWidth: 0,
  },
  resizeHandle: {
    flexShrink: 0,
    width: theme.spacing(1),
    cursor: 'col-resize',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    '&::after': {
      content: '""',
      width: 1,
      backgroundColor: theme.palette.labraChat.borderMuted,
    },
    '&:hover::after, &:focus-visible::after': {
      backgroundColor: theme.palette.labraAIGradient.start,
    },
  },
  previewPane: {
    flex: 1,
  },
  previewPaneDragging: {
    pointerEvents: 'none',
  },
  body: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    '&:hover .chatBackButton': {
      visibility: 'visible',
    },
  },
  backButton: {
    position: 'absolute',
    top: theme.spacing(1),
    left: theme.spacing(1),
    zIndex: 1,
    visibility: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(3),
    height: theme.spacing(3),
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 14,
    color: theme.palette.labraText.description,
    '&:hover': {
      color: theme.palette.labraText.tertiary,
    },
  },
  bodyCentered: {
    justifyContent: 'center',
    overflowY: 'auto',
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    padding: theme.spacing(3, 3, 0, 3),
    gap: theme.spacing(3),
  },
  scrollAreaCentered: {
    flex: '0 0 auto',
    overflowY: 'visible',
    alignItems: 'center',
  },
  bottomSection: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(0, 6.75, 1.5, 6.75),
  },
  bottomSectionCentered: {
    width: '100%',
    maxWidth: theme.spacing(57.5),
    alignSelf: 'center',
    paddingTop: theme.spacing(3),
  },
  summary: {
    display: 'block',
    maxWidth: theme.spacing(55.375),
    margin: '0 auto',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: 24,
    lineHeight: '32px',
    textAlign: 'center',
    color: theme.palette.labraText.description,
  },
  // Pulsing dot that marks the counts as something waiting on the user.
  dot: {
    display: 'inline-block',
    position: 'relative',
    width: theme.spacing(1),
    height: theme.spacing(1),
    marginRight: theme.spacing(1),
    borderRadius: '50%',
    backgroundColor: theme.palette.error.dark,
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      borderRadius: '50%',
      backgroundColor: theme.palette.error.dark,
    },
    '&::before': {
      width: theme.spacing(1.667),
      height: theme.spacing(1.667),
      opacity: 0.2,
    },
    '&::after': {
      width: theme.spacing(2.25),
      height: theme.spacing(2.25),
      opacity: 0.15,
    },
  },
  count: {
    border: 'none',
    background: 'none',
    font: 'inherit',
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: 6,
    backgroundImage: `linear-gradient(105.67deg, ${CHAT_GRADIENT_START} 0%, ${CHAT_GRADIENT_MIDDLE} 56.26%, ${CHAT_GRADIENT_END} 101.76%)`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    // The hover box hugs the text: padding gives it room, and the matching
    // negative margin keeps the sentence spaced by its own single space rather
    // than a space plus the padding.
    padding: `${theme.spacing(0.25)} ${theme.spacing(0.75)}`,
    margin: `0 -${theme.spacing(0.75)}`,
    position: 'relative',
    // The hover ground has to live on a pseudo-element: `background-clip: text`
    // above clips *every* background on this element to the glyphs, so a plain
    // background-color would paint inside the letters and never be seen.
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: -1,
      borderRadius: 6,
      backgroundColor: 'transparent',
      transition: 'background-color 120ms',
    },
    '&:hover::before': {
      backgroundColor: theme.palette.labraChat.hoverBg,
    },
  },
  greeting: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: 22,
    lineHeight: '28px',
    textAlign: 'center',
    color: theme.palette.labraText.description,
  },
}))
